import { useRouteResponse } from "@src/app/hooks/useRouteResponse";
import type {
  BFFArticleBodyElement,
  BFFArticleCard,
  BFFResponseType,
} from "@src/app/types/bff";
import type { ArticlePageResponse } from "@src/app/types/response";
import { Fragment, useEffect, useState } from "react";

import GoogleAd, { GoogleAdTypeEnum } from "../_ads/GoogleAd/GoogleAd";
import ArticleEmbed from "./body/ArticleEmbed";
import ArticleHorizonRow from "./body/ArticleHorizontalRow";
import ArticleImage from "./body/ArticleImage";
import ArticleInterview from "./body/ArticleInterview";
import ArticleListBulleted from "./body/ArticleListBulleted";
import ArticleListNumbered from "./body/ArticleListNumbered";
import ArticleMedia from "./body/ArticleMedia";
import ArticleParagraph from "./body/ArticleParagraph";
import ArticlePullQuote from "./body/ArticlePullQuote";
import ArticleRelation from "./body/ArticleRelation";
import ArticleSubHead from "./body/ArticleSubHead";
import ArticleTable from "./body/ArticleTable";
import ArticleYoutube from "./body/ArticleYoutube";

export const mapBodyElementLayout = (
  element: BFFArticleBodyElement[keyof BFFArticleBodyElement],
  options?: {
    /** latest articles in Relation Element */
    relationArticles?: BFFArticleCard[];
  },
) => {
  if (element.type == "paragraph") {
    return <ArticleParagraph element={element} />;
  }
  if (element.type == "image") {
    return <ArticleImage element={element} />;
  }
  if (element.type == "embed") {
    return <ArticleEmbed element={element} />;
  }
  if (element.type == "horizontal_row") {
    return <ArticleHorizonRow element={element} />;
  }
  if (element.type == "sub_head") {
    return <ArticleSubHead element={element} />;
  }
  if (element.type == "youtube_video") {
    return <ArticleYoutube element={element} />;
  }
  if (element.type == "list_bulleted") {
    return <ArticleListBulleted element={element} />;
  }
  if (element.type == "list_numbered") {
    return <ArticleListNumbered element={element} />;
  }
  if (element.type == "pull_quote") {
    return <ArticlePullQuote element={element} />;
  }
  if (element.type == "media") {
    return <ArticleMedia element={element} />;
  }
  // Uncomment if map support is added in the future
  // if (element.type == "map") {
  //   return <ArticleMap element={element} />;
  // }
  if (element.type == "table") {
    return <ArticleTable element={element} />;
  }
  if (element.type == "interview") {
    return <ArticleInterview element={element} />;
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (element.type == "relation") {
    return (
      <ArticleRelation
        element={element}
        relationArticles={options?.relationArticles || []}
      />
    );
  }
  return null;
};

export default function ArticleBody() {
  const { article } = useRouteResponse("articlePage");

  const { bodyElements = [] } = article || {};

  const _bodyElements = bodyElements
    .map((i, index) => ({ ...i, _index: index }))
    .filter((i) => i.type == "paragraph");

  const insertIndex = _bodyElements[2]?._index ?? -1; // imu1 after 3rd paragraph
  const insertIndex2 = _bodyElements[5]?._index ?? -1; // midarticlespecial after 6th paragraph

  const [relationArticles, setRelationArticles] = useState<BFFArticleCard[]>(
    [],
  );

  const getInfo = async () => {
    if (!article?.id) {
      console.warn("No article ID found for fetching recommended articles.");
      return;
    }
    try {
      const res = await fetch(
        `/_plat/api/v1/client/relationArticles/${article.id}`,
      );
      const data = (await res.json()) as BFFResponseType<ArticlePageResponse>;

      setRelationArticles(data.response.relationArticles || []);
    } catch (error) {
      console.error("Error fetching relation articles:", error);
    }
  };

  useEffect(() => {
    void getInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isImu1Empty, setIsImu1Empty] = useState(true);
  const [isMiddleEmpty, setIsMiddleEmpty] = useState(true);

  return bodyElements.flatMap((element, index) => {
    if (index == insertIndex) {
      return [
        <Fragment key={`${element.type}-${index}`}>
          {mapBodyElementLayout(element, { relationArticles })}
        </Fragment>,
        <div
          className="pb-4"
          key="google-ads-imu1"
          style={{ display: isImu1Empty ? "none" : "block" }}
        >
          <GoogleAd
            onSlotRenderEnded={(e) => {
              e.isEmpty ? setIsImu1Empty(true) : setIsImu1Empty(false);
            }}
            className="border-y border-divider-primary bg-tertiary py-2"
            type={GoogleAdTypeEnum.IMU1}
          />
        </div>,
      ];
    }
    if (index == insertIndex2) {
      return [
        <Fragment key={`${element.type}-${index}`}>
          {mapBodyElementLayout(element, { relationArticles })}
        </Fragment>,
        <div
          className="pb-4"
          key="google-ads-midarticlespecial"
          style={{ display: isMiddleEmpty ? "none" : "block" }}
        >
          <GoogleAd
            onSlotRenderEnded={(e) => {
              e.isEmpty ? setIsMiddleEmpty(true) : setIsMiddleEmpty(false);
            }}
            className="border-y border-divider-primary bg-tertiary py-2"
            type={GoogleAdTypeEnum.MIDARTICLE}
          />
        </div>,
      ];
    }
    return (
      <Fragment key={`${element.type}-${index}`}>
        {mapBodyElementLayout(element, { relationArticles })}
      </Fragment>
    );
  });
}
